declare const process: { env: Record<string, string | undefined> } | undefined;

export enum Environment {
  Dev = 'dev',
  // one server per open pull request, served on demand at https://pr-<number>.singyourpart.app
  // (GitHub issue #3412). Like dev it listens on SYP_SERVER_PORT over plain HTTP and serves the
  // bundle its own checkout built; like test it is a public URL holding a copy of production data,
  // so it inherits every "not production" guard and is never treated as dev by the client.
  Preview = 'preview',
  Test = 'test',
  Production = 'production',
  Automation = 'automation',
}

export const SUPPRESS_DB_TESTS = 'SUPPRESS_DB_TESTS';

// the main checkout's dev port; each worktree runs on a port derived from its GitHub issue
// number, passed to the server as SYP_SERVER_PORT (see scripts/sypdev.mjs). One port serves
// the API and the built client alike (issue #3447).
export const devServerPort = 3001;

// a preview's public hostname: pr-<pull request number>.singyourpart.app (GitHub issue #3412)
const previewHostnamePattern = /^pr-\d+\.singyourpart\.app$/;

// works on client or server
export function getBaseUrl(environment?: Environment): string {
  const env = environment ?? getEnvironment();
  switch (env) {
    case Environment.Production:
      return 'https://singyourpart.app';
    case Environment.Test:
      return 'https://test.singyourpart.app';
    case Environment.Preview: {
      if (inBrowser()) {
        return window.location.origin;
      }
      // Every preview has a different public URL, so the prep script writes this PR's
      // https://pr-<number>.singyourpart.app into .env.preview as SYP_BASE_URL. Deliberately no
      // localhost fallback: this is what invite links and Stripe return URLs are built from, and a
      // preview quietly handing out http://localhost links would look like it worked. server.ts
      // reads it at startup so a missing value fails the boot, where the preview's log will show it.
      const baseUrl = process?.env?.SYP_BASE_URL;
      if (!baseUrl) {
        throw new Error(
          'SYP_BASE_URL is not set - a preview server needs its own base URL (see README "Environment Variables")',
        );
      }
      return baseUrl;
    }
    default:
      if (inBrowser()) {
        return window.location.origin;
      }
      return `http://localhost:${process?.env?.SYP_SERVER_PORT ?? devServerPort}`;
  }
}

export function getEnvironment(): Environment {
  if (inBrowser()) {
    // used singyourpart.org at the ACCS conference in June 2025 to bypass cached apps
    if (window.location.hostname === 'singyourpart.app' || window.location.hostname === 'singyourpart.org') {
      return Environment.Production;
    }
    if (window.location.hostname === 'test.singyourpart.app') {
      return Environment.Test;
    }
    // a per-PR preview is a public URL serving a copy of production data, so the client must not
    // run in dev mode there (no dev-only logging, no dev bypass of the internal pages)
    if (previewHostnamePattern.test(window.location.hostname)) {
      return Environment.Preview;
    }
    return Environment.Dev;
  }
  if (!process?.env?.ENVIRONMENT) {
    return Environment.Dev;
  }
  return process.env.ENVIRONMENT as Environment;
}

export function inBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function inSafari(): boolean {
  return Boolean(
    navigator.vendor &&
    navigator.vendor.indexOf('Apple') > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf('CriOS') == -1 &&
    navigator.userAgent.indexOf('FxiOS') == -1,
  );
}
